import toast from 'react-hot-toast'
import { BaseModal } from 'components/organisms/modals/base-modal'
import { Button } from 'components/atoms/button'
import {
	AllowCreateEventType,
	useRequestCreateEventApprovalMutation,
	useRequestCreateEventTypeApprovalMutation,
} from 'graphql/generated/scheme'
import { trueErrorMessage } from 'utils/error'

interface Props {
	open: boolean
	setOpen: (open: boolean) => void
	/** When set, requests approval for this specific event type instead of general event creation. */
	type?: AllowCreateEventType
	/** Human-readable label for `type`, used in the modal copy (e.g. "contest"). */
	eventTypeLabel?: string
	/** Overrides the modal heading. */
	title?: string
	/** Overrides the modal body copy. */
	description?: string
}

export default function CreateEventRestrictedModal({ open, setOpen, type, eventTypeLabel, title, description }: Props) {
	const [requestApproval, { loading: loadingApproval }] = useRequestCreateEventApprovalMutation()
	const [requestTypeApproval, { loading: loadingTypeApproval }] = useRequestCreateEventTypeApprovalMutation()
	const loading = type ? loadingTypeApproval : loadingApproval

	const handleClose = () => setOpen(false)

	const handleRequest = async () => {
		try {
			if (type) {
				await requestTypeApproval({ variables: { type } })
			} else {
				await requestApproval()
			}
			toast.success("Request sent! We'll reach out to you soon.")
			handleClose()
		} catch (err) {
			toast.error(trueErrorMessage(err))
		}
	}

	return (
		<BaseModal open={open} setOpen={handleClose} title={title ?? 'Event Creation Restricted'} hideCloseButton>
			<div className="space-y-4">
				<p className="text-secondary-text text-sm leading-relaxed text-pretty">
					{description ??
						`You're currently restricted from creating ${
							eventTypeLabel ? `${eventTypeLabel} events` : 'events'
						}. If you would like to get approved, contact us and we will reach out soon.`}
				</p>

				<div className="flex gap-3 pt-2">
					<Button variant="outline" fullWidth onClick={handleClose}>
						Close
					</Button>
					<Button variant="primary" fullWidth onClick={handleRequest} disabled={loading}>
						{loading ? 'Sending...' : 'Send Request'}
					</Button>
				</div>
			</div>
		</BaseModal>
	)
}
